import React, { useState, useEffect } from "react";
import FaqSection from "../components/FaqSection";
import { emsFaq } from "../seo/pageMeta";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import "./EMSPage.css";
import emsImage from "../ems-banner.jpg";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";

// Same manufacturing-infrastructure section shown on the home page
// (equipment / facility / process / capacity), reused here as requested.
import ManufacturingExcellence from "../ManufacturingExcellence";

/* =====================
   ANIMATION VARIANTS
===================== */
const fadeUp = {
  hidden:  { opacity: 0, y: 36 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] } },
};

const staggerList = {
  hidden:  {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.15 } },
};

const listItem = {
  hidden:  { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0,  transition: { duration: 0.45, ease: "easeOut" } },
};

const VIEW = { once: true, amount: 0.25 };

/* =====================
   ICONS (inline SVG)
===================== */
const CheckIcon = () => (
  <svg viewBox="0 0 12 12" width="10" height="10">
    <polyline points="2,6 5,9 10,3" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

const icons = {
  quality:   <circle cx="12" cy="12" r="10"/>,
  speed:     <polygon points="12 2 2 7 12 12 22 7 12 2"/>,
  precision: <circle cx="12" cy="12" r="3"/>,
  support:   <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>,
  tech:      <><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></>,
  delivery:  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>,
};

const BenefitIcon = ({ name }) => (
  <div className="ems-benefit-icon">
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      {icons[name] || icons.tech}
    </svg>
  </div>
);

/* =====================
   STATIC DATA
===================== */
const SERVICES = [
  {
    id: 1,
    slug: "pcb-assembly",
    label: "Core Offering",
    title: "PCB",
    titleAccent: "Assembly",
    description:
      "Leveraging advanced Surface-Mount Technology (SMT) and through-hole techniques, we deliver high-precision PCB assemblies. Our ISO9001-certified processes ensure reliability for automotive, medical, and consumer electronics.",
    offerings: [
      "Surface-Mount Technology (SMT)",
      "Through-Hole Assembly (THA)",
      "Mixed Technology Solutions",
      "ISO9001 & RoHS Compliance",
      "Rigorous Quality Testing",
    ],
    img: "/WebPictures/PCBAssembly.jpg",
    badge: "Advanced SMT & THA",
  },
  {
    id: 2,
    slug: "turnkey-assembly",
    label: "Complete Solution",
    title: "Turnkey",
    titleAccent: "Assembly",
    description:
      "BIBA Electronics offers a comprehensive turnkey solution, integrating advanced PCB assembly, custom TFT/LCD display design, BOM procurement, and system integration. Rapid prototyping and rigorous testing deliver tailored solutions.",
    offerings: [
      "End-to-End Project Management",
      "Component Procurement & BOM",
      "PCB Assembly & Integration",
      "TFT/LCD Display Integration",
      "System Testing & Validation",
    ],
    img: "/WebPictures/TurnkeyAssembly.jpg",
    badge: "Full-Service Integration",
  },
  {
    id: 3,
    slug: "consigned-assembly",
    label: "Flexible Partnership",
    title: "Consigned",
    titleAccent: "Assembly",
    description:
      "With Consigned PCB Assembly, you supply parts and components while BIBA handles fabrication and assembly. This flexible model ensures high-quality boards built to meet your exact application requirements and specifications.",
    offerings: [
      "Client-Supplied Component Management",
      "Flexible Manufacturing Schedule",
      "Transparent Cost Structure",
      "Quality Assurance & Testing",
      "Detailed Assembly Documentation",
    ],
    img: "/WebPictures/ConsignedAssembly.jpg",
    badge: "Client-Centric Approach",
  },
];

const BENEFITS = [
  { icon: "quality",   title: "ISO9001 Certified",        desc: "Industry-standard quality assurance ensures every assembly meets strict reliability benchmarks.",     featured: false },
  { icon: "precision", title: "Advanced Precision",       desc: "SMT technology achieves micro-level accuracy for complex board designs and tight tolerances.",      featured: true  },
  { icon: "speed",     title: "Rapid Turnaround",         desc: "Streamlined processes and in-house capabilities accelerate project timelines without compromising quality.",  featured: false },
  { icon: "tech",      title: "Multi-Technology Support", desc: "We handle SMT, through-hole, and mixed assemblies — no constraint on your design complexity.",       featured: false },
  { icon: "support",   title: "End-to-End Support",       desc: "From design review and prototyping to production scaling, we partner with you at every step.",      featured: false },
  { icon: "delivery",  title: "Reliable Delivery",        desc: "Just-in-time procurement and optimized logistics ensure components and assemblies arrive on schedule.", featured: false },
];

/* =====================
   IMAGE WRAPPER
===================== */
const ServiceImage = ({ src, alt, badge }) => (
  <div className="ems-service-image-wrap">
    <img
      src={src}
      alt={alt}
      loading="lazy"
      decoding="async"
      onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
    />
    <div className="ems-service-img-placeholder" style={{ display: "none" }}>
      <span className="ph-icon">🏭</span>
      <span className="ph-text">{alt}</span>
      <span className="ph-path">{src}</span>
    </div>
    <div className="ems-service-image-badge">
      <span className="badge-dot" />
      {badge}
    </div>
  </div>
);

/* =====================
   MAIN COMPONENT
===================== */
const DEFAULT_HERO_DURATION_MS = 5000;

const EMSPage = () => {
  const [heroBanners, setHeroBanners] = useState([{ src: emsImage, durationMs: DEFAULT_HERO_DURATION_MS }]);
  const [heroIndex, setHeroIndex] = useState(0);
  const [searchParams] = useSearchParams();

  // Marquee images array
  const marqueeImages = Array.from({ length: 29 }, (_, i) => `/components/${i + 1}.png`);

  // Fetches the home_banner docs whose location === "Electronics
  // Manufacturing" and status === true, then swaps them in for the default
  // local hero image. Each banner carries its own on-screen duration
  // (durationSeconds in Firestore, set from Tixaru's Biba Website >
  // Banners page), defaulting to DEFAULT_HERO_DURATION_MS for banners
  // saved before that field existed.
  useEffect(() => {
    const loadBanner = async () => {
      try {
        const bannerQuery = query(
          collection(db, "home_banner"),
          where("location", "==", "Electronics Manufacturing"),
          where("status", "==", true)
        );
        const snapshot = await getDocs(bannerQuery);

        const banners = snapshot.docs
          .map((doc) => doc.data())
          .sort((a, b) => {
            const orderDiff = (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0);
            return orderDiff !== 0 ? orderDiff : (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0);
          })
          .map((banner) => ({
            src: banner.image,
            durationMs: (Number(banner.durationSeconds) || DEFAULT_HERO_DURATION_MS / 1000) * 1000,
          }))
          .filter((banner) => Boolean(banner.src));

        if (banners.length > 0) {
          setHeroBanners(banners);
          setHeroIndex(0);
        }
      } catch (error) {
        console.error(error);
      }
    };

    loadBanner();
  }, []);

  // Auto-swap between hero banners when there's more than one for this
  // section, each shown for its own duration.
  useEffect(() => {
    if (heroBanners.length < 2) return undefined;
    const duration = heroBanners[heroIndex]?.durationMs ?? DEFAULT_HERO_DURATION_MS;
    const timer = setTimeout(() => {
      setHeroIndex((i) => (i + 1) % heroBanners.length);
    }, duration);
    return () => clearTimeout(timer);
  }, [heroBanners, heroIndex]);

  const heroImage = heroBanners[heroIndex]?.src ?? heroBanners[0].src;

  // Deep link (?service=pcb-assembly) — scroll straight to that card.
  useEffect(() => {
    const serviceSlug = searchParams.get("service");
    if (!serviceSlug) return;

    setTimeout(() => {
      document.getElementById(`service-${serviceSlug}`)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  }, [searchParams]);

  return (
    <div className="ems-page">
      {/* Hero Section */}
      <div className="ems-hero">
        <img src={heroImage} alt="Electronic Manufacturing Services" />

        {/* Overlay with Title - positioned absolutely at bottom */}
        <div className="ems-overlay">
          <h1>Electronic Manufacturing Services</h1>
        </div>
      </div>

      {/* Breadcrumbs - sit below the hero banner */}
      <div className="ems-breadcrumb-bar">
        <div className="ems-breadcrumbs">
          <a href="/">Home</a>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">Electronic Manufacturing Services</span>
        </div>
      </div>

      {/* ===========================
          MARQUEE — Component Showcase
      =========================== */}
      <section className="ems-marquee-section">
        <motion.div
          className="ems-marquee-label"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={VIEW}
        >
          <span>Components We Assemble</span>
        </motion.div>

        <motion.div
          className="ems-marquee"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="ems-marquee-track">
            {marqueeImages.concat(marqueeImages).map((src, index) => (
              <div key={`${src}-${index}`} className="ems-marquee-item">
                <img src={src} alt={`Component ${(index % marqueeImages.length) + 1}`} loading="lazy" decoding="async" />
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ===========================
          SERVICES — PCB / Turnkey / Consigned Assembly, side by side
      =========================== */}
      <div className="ems-services-wrapper">
        <div className="ems-services-grid">
          {SERVICES.map((svc, idx) => (
            <motion.div
              key={svc.id}
              id={`service-${svc.slug}`}
              className="ems-service-card"
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={VIEW}
              transition={{ duration: 0.5, delay: idx * 0.1, ease: "easeOut" }}
            >
              <ServiceImage
                src={svc.img}
                alt={`${svc.title} ${svc.titleAccent}`}
                badge={svc.badge}
              />

              <div className="ems-service-content">
                <span className="ems-service-label">{svc.label}</span>
                <h2>
                  {svc.title} <span>{svc.titleAccent}</span>
                </h2>
                <p>{svc.description}</p>

                <ul className="ems-service-list" style={{ listStyle: "none" }}>
                  {svc.offerings.map((item) => (
                    <li key={item} className="ems-service-list-item">
                      <span className="ems-check-icon"><CheckIcon /></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* ===========================
          MANUFACTURING EXCELLENCE — same section shown on the home page
      =========================== */}
      <ManufacturingExcellence />

      {/* ===========================
          WHY CHOOSE BIBA — Benefits Grid
      =========================== */}
      <section className="ems-benefits-section">
        <div className="ems-benefits-inner">
          <motion.div
            className="ems-benefits-header"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={VIEW}
          >
            <span className="ems-section-tag">Why Choose BIBA</span>
            <h2>
              Industry-Leading<br />
              <span>Quality &amp; Reliability</span>
            </h2>
            <p>
              Six reasons why global manufacturers trust BIBA Electronics
              for their most critical production and assembly needs.
            </p>
          </motion.div>

          <motion.div
            className="ems-benefits-grid"
            variants={staggerList}
            initial="hidden"
            whileInView="visible"
            viewport={VIEW}
          >
            {BENEFITS.map((b) => (
              <motion.div
                key={b.title}
                className={`ems-benefit-card${b.featured ? " featured" : ""}`}
                variants={listItem}
                whileHover={{ y: -4 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
              >
                <BenefitIcon name={b.icon} />
                <h3>{b.title}</h3>
                <p>{b.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <FaqSection faq={emsFaq} />

    </div>
  );
};

export default EMSPage;